"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { useRouter } from "next/navigation"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { CheckCircle } from "lucide-react"
import { Textarea } from "@/components/ui/textarea"
import Image from "next/image"
import { useSendQuizResult } from "@/lib/api/send-quiz-result/use-mutation"

// Define the quiz structure
const QUIZ_STEPS = {
    buildingType: {
        title: "Какой тип постройки вас интересует?",
        image: "/quiz/type.jpg",
        options: [
            {
                id: "domik",
                value: "Дачный домик",
                label: "Дачный домик",
                image: "/quiz/type-domik.jpg",
            },
            {
                id: "bitovka",
                value: "Бытовка",
                label: "Бытовка",
                image: "/quiz/type-bitovka.jpg",
            },
            {
                id: "hozblok",
                value: "Хозблок",
                label: "Хозблок",
                image: "/quiz/type-hozblok.jpg",
            },
        ],
    },
    purposeType: {
        title: "Какая цель использования?",
        image: "/quiz/purpose.jpg",
        options: [
            {
                id: "living",
                value: "Проживание",
                label: "Проживание",
                image: "/quiz/purpose-living.jpg",
            },
            {
                id: "storage",
                value: "Хранение",
                label: "Хранение",
                image: "/quiz/purpose-storage.jpg",
            },
            {
                id: "other",
                value: "Другое",
                label: "Другое",
                image: "/quiz/purpose-other.jpg",
            },
        ],
    },
    foundationType: {
        title: "Какой тип фундамента предпочитаете?",
        image: "/quiz/foundation.jpg",
        options: [
            {
                id: "blocks",
                value: "На блоках",
                label: "На блоках",
                image: "/quiz/foundation-blocks.jpg",
            },
            {
                id: "piles",
                value: "На сваях",
                label: "На сваях",
                image: "/quiz/foundation-piles.jpg",
            },
        ],
    },
} satisfies Record<
    string,
    {
        title: string
        image: string
        options: Array<{
            id: string
            value: string
            label: string
            image: string
        }>
    }
>

type FormData = {
    "Тип постройки": string
    "Цель использования": string
    purposeDetails?: string
    "Тип фундамента": string
    "Имя клиента": string
    "Телефон клиента": string
    "Адрес доставки": string
}

export function QuizDialog({
    children,
}: {
    children: React.ReactNode
}) {
    const [step, setStep] = useState(1)
    const [open, setOpen] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [currentImage, setCurrentImage] = useState(
        QUIZ_STEPS.buildingType.image,
    )
    const router = useRouter()

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormData>()

    // Load stored values from localStorage on client side only
    useEffect(() => {
        setValue("Имя клиента", localStorage.getItem("customerName") || "")
        setValue("Телефон клиента", localStorage.getItem("customerPhone") || "")
        setValue(
            "Адрес доставки",
            localStorage.getItem("customerAddress") || "",
        )
    }, [setValue])

    const currentStepKey =
        step === 1
            ? "buildingType"
            : step === 2
              ? "purposeType"
              : "foundationType"
    const currentStepData = QUIZ_STEPS[currentStepKey]

    // Watch form values for image updates
    const watchBuildingType = watch("Тип постройки")
    const watchPurposeType = watch("Цель использования")
    const watchFoundationType = watch("Тип фундамента")

    // Update current image based on step and selection
    useEffect(() => {
        // Always start with the default image for the current step
        let newImage = QUIZ_STEPS[currentStepKey].image

        // If an option is selected, use its image instead
        if (step === 1 && watchBuildingType) {
            const option = QUIZ_STEPS.buildingType.options.find(
                (opt) => opt.value === watchBuildingType,
            )
            if (option?.image) {
                newImage = option.image
            }
        } else if (step === 2 && watchPurposeType) {
            const option = QUIZ_STEPS.purposeType.options.find(
                (opt) => opt.value === watchPurposeType,
            )
            if (option?.image) {
                newImage = option.image
            }
        } else if (step === 3 && watchFoundationType) {
            const option = QUIZ_STEPS.foundationType.options.find(
                (opt) => opt.value === watchFoundationType,
            )
            if (option?.image) {
                newImage = option.image
            }
        } else if (step === 4) {
            newImage = "/quiz/final.jpg"
        }

        setCurrentImage(newImage)
    }, [
        step,
        watchBuildingType,
        watchPurposeType,
        watchFoundationType,
        currentStepKey,
    ])

    const { mutateAsync: sendQuizResult } = useSendQuizResult()

    const onSubmit = async (data: FormData) => {
        setIsSubmitting(true)
        try {
            // Save contact info to localStorage
            localStorage.setItem("customerName", data["Имя клиента"])
            localStorage.setItem("customerPhone", data["Телефон клиента"])
            localStorage.setItem("customerAddress", data["Адрес доставки"])

            // Prepare the quiz results, handling the "Другое" case
            const quizResults = {
                "Тип постройки": data["Тип постройки"],
                "Цель использования":
                    data["Цель использования"] === "Другое"
                        ? data.purposeDetails || "Другое" // Fallback to "Другое" if no details provided
                        : data["Цель использования"],
                "Тип фундамента": data["Тип фундамента"],
            }

            await sendQuizResult({
                customer_name: data["Имя клиента"],
                customer_phone: data["Телефон клиента"],
                customer_address: data["Адрес доставки"],
                quiz_results_map: quizResults,
            })

            handleOpenChange(false)
            router.push("/thanks")
        } catch (error) {
            console.error("Failed to submit quiz:", error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const resetForm = () => {
        setStep(1)
        setCurrentImage(QUIZ_STEPS.buildingType.image)
    }

    const handleOpenChange = (open: boolean) => {
        setOpen(open)
        if (!open) {
            resetForm()
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogTitle className="sr-only">Ответьте на вопросы</DialogTitle>
            <DialogContent className="min-h-[440px] overflow-hidden p-0 sm:max-w-[700px]">
                <div className="flex h-full flex-col md:flex-row">
                    {/* Изображение (слева на десктопе, сверху на мобильных) */}
                    <div className="relative h-[200px] md:h-full md:w-2/5">
                        <Image
                            src={currentImage}
                            alt="Изображение"
                            fill
                            className="object-cover"
                            priority={step === 1}
                            sizes="(max-width: 768px) 100vw, 40vw"
                            quality={90}
                        />
                    </div>

                    {/* Форма (справа на десктопе, снизу на мобильных) */}
                    <div className="flex flex-col p-6 md:w-3/5">
                        <DialogHeader>
                            <DialogTitle className="mb-4 text-center text-xl">
                                {step === 4
                                    ? "Оставьте контактные данные"
                                    : currentStepData.title}
                            </DialogTitle>
                        </DialogHeader>

                        <form
                            onSubmit={(e) => {
                                // Prevent native form submission
                                e.preventDefault()
                                // Only submit if we're on the last step
                                if (step === 4) {
                                    handleSubmit(onSubmit)()
                                }
                            }}
                            className="flex flex-1 flex-col"
                            onKeyDown={(e) => {
                                // Handle Enter key for navigation
                                if (e.key === "Enter") {
                                    e.preventDefault()
                                    if (step < 4) {
                                        setStep((prev) => prev + 1)
                                    } else if (step === 4) {
                                        handleSubmit(onSubmit)()
                                    }
                                }
                            }}
                        >
                            <div className="flex-1">
                                {step < 4 && (
                                    <RadioGroup
                                        value={watch(
                                            currentStepKey === "buildingType"
                                                ? "Тип постройки"
                                                : currentStepKey ===
                                                    "purposeType"
                                                  ? "Цель использования"
                                                  : "Тип фундамента",
                                        )}
                                        onValueChange={(value) => {
                                            setValue(
                                                currentStepKey ===
                                                    "buildingType"
                                                    ? "Тип постройки"
                                                    : currentStepKey ===
                                                        "purposeType"
                                                      ? "Цель использования"
                                                      : "Тип фундамента",
                                                value,
                                            )
                                        }}
                                        className="space-y-3"
                                    >
                                        {currentStepData.options.map(
                                            (option) => (
                                                <Label
                                                    key={option.id}
                                                    htmlFor={option.id}
                                                    className="flex cursor-pointer items-center space-x-2 border p-3"
                                                >
                                                    <RadioGroupItem
                                                        value={option.value}
                                                        id={option.id}
                                                    />
                                                    <span className="flex-1">
                                                        {option.label}
                                                    </span>
                                                </Label>
                                            ),
                                        )}
                                    </RadioGroup>
                                )}

                                {step === 2 &&
                                    watchPurposeType === "Другое" && (
                                        <div className="pt-2">
                                            <Textarea
                                                placeholder="Укажите цель использования"
                                                {...register("purposeDetails")}
                                            />
                                        </div>
                                    )}

                                {step === 4 && (
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="Имя клиента">
                                                Ваше имя{" "}
                                                <span className="opacity-50">
                                                    (необязательно)
                                                </span>
                                            </Label>
                                            <Input
                                                id="Имя клиента"
                                                autoFocus
                                                {...register("Имя клиента", {
                                                    required: false,
                                                })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="Телефон клиента">
                                                Номер телефона
                                            </Label>
                                            <Input
                                                id="Телефон клиента"
                                                type="tel"
                                                placeholder="+7 (___) ___-__-__"
                                                {...register(
                                                    "Телефон клиента",
                                                    {
                                                        required: true,
                                                    },
                                                )}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="Адрес доставки">
                                                Адрес доставки{" "}
                                                <span className="opacity-50">
                                                    (необязательно)
                                                </span>
                                            </Label>
                                            <Input
                                                id="Адрес доставки"
                                                {...register("Адрес доставки", {
                                                    required: false,
                                                })}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-between pt-4">
                                {step > 1 && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() =>
                                            setStep((prev) => prev - 1)
                                        }
                                    >
                                        Назад
                                    </Button>
                                )}
                                {step < 4 ? (
                                    <Button
                                        type="button"
                                        className="ml-auto min-w-[120px]"
                                        onClick={() =>
                                            setStep((prev) => prev + 1)
                                        }
                                        disabled={
                                            !watch(
                                                currentStepKey ===
                                                    "buildingType"
                                                    ? "Тип постройки"
                                                    : currentStepKey ===
                                                        "purposeType"
                                                      ? "Цель использования"
                                                      : "Тип фундамента",
                                            )
                                        }
                                    >
                                        Далее
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        className="ml-auto"
                                        onClick={() => handleSubmit(onSubmit)()}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <div className="flex items-center gap-2">
                                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                                <span>Отправка...</span>
                                            </div>
                                        ) : (
                                            "Заказать расчёт"
                                        )}
                                    </Button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
